import React from "react";
import { Star, BadgeCheck, Loader2, Inbox, Languages } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { STATUS_LABELS } from "../lib/format";

export function Rating({ value = 0, count, size = 14, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid="rating">
      <Star size={size} className="fill-lime text-lime" strokeWidth={2} />
      <span className="font-semibold text-ink">{Number(value).toFixed(1)}</span>
      {count != null && <span className="text-muted-foreground text-xs">({count})</span>}
    </span>
  );
}

export function VerifiedBadge({ status = "verified", showLabel = true }) {
  if (status !== "verified") return null;
  return (
    <span className="inline-flex items-center gap-1 text-sky font-semibold text-xs" data-testid="verified-badge">
      <BadgeCheck size={15} className="text-sky" strokeWidth={2.5} />
      {showLabel && "Verified"}
    </span>
  );
}

const STATUS_COLORS = {
  PAYMENT_COMPLETED: "bg-sky-soft text-sky", ITEM_PURCHASED: "bg-lime-light text-navy",
  TRAVELING: "bg-sky-soft text-sky", ARRIVED: "bg-lime-light text-navy",
  LOCAL_DELIVERY: "bg-sky-soft text-sky", COMPLETED: "bg-lime text-navy",
  DISPUTED: "bg-red-100 text-red-600", CANCELLED: "bg-gray-100 text-gray-500",
  REFUNDED: "bg-orange-100 text-orange-600",
  open: "bg-lime-light text-navy", pending: "bg-orange-100 text-orange-600",
  request_sent: "bg-sky-soft text-sky", quotation_sent: "bg-sky-soft text-sky",
  paid: "bg-lime text-navy", active: "bg-lime-light text-navy",
};

export function StatusBadge({ status, className = "" }) {
  const label = STATUS_LABELS[status] || (status || "").replace(/_/g, " ");
  const color = STATUS_COLORS[status] || "bg-gray-100 text-gray-600";
  return (
    <span data-testid={`status-${status}`}
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${color} ${className}`}>
      {label}
    </span>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6" data-testid="empty-state">
      <div className="w-16 h-16 rounded-2xl bg-white shadow-soft flex items-center justify-center mb-4">
        <Icon className="text-navy" size={28} strokeWidth={2} />
      </div>
      <h3 className="font-heading font-bold text-lg text-ink">{title}</h3>
      {subtitle && <p className="text-muted-foreground text-sm mt-1 max-w-xs">{subtitle}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Spinner({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground" data-testid="spinner">
      <Loader2 className="animate-spin" size={20} />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

export function LangToggle() {
  const { lang, toggle } = useI18n();
  return (
    <button onClick={toggle} data-testid="lang-toggle"
      className="inline-flex items-center gap-1 rounded-full border border-black/10 bg-white px-2.5 py-1.5 text-xs font-semibold text-navy hover:bg-cloud transition-colors">
      <Languages size={14} /> {lang.toUpperCase()}
    </button>
  );
}

export function SectionTitle({ children, action }) {
  return (
    <div className="flex items-end justify-between mb-4">
      <h2 className="font-heading font-extrabold tracking-tight text-xl sm:text-2xl text-navy">{children}</h2>
      {action}
    </div>
  );
}
